import React, { useEffect, useState, Suspense, lazy } from 'react';
import { logAuthFailure } from './services/PreAuthLogService';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Tom2LockProvider, useTom2Lock } from './contexts/Tom2LockContext';
import { ExercisesProvider, useExercises } from './contexts/ExercisesContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AdminGuard from './admin/AdminGuard';
import { NotationProvider } from './contexts/NotationContext';
import { ParcoursProvider } from './contexts/ParcoursContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { hasAccessToExercise, hasAccessToChallenge, getAccessDeniedMessage, isRestrictedAccount, getRestrictionReason, isPathAllowedForRestricted, isAnonymousUser } from './utils/accessControl';
import AccessDeniedModal from './components/AccessDeniedModal';
import * as Tone from 'tone';
import { initAudio } from './utils/audioContextManager';
import { reloadIfStale } from './utils/staleVersion';
import { applyActionCode } from 'firebase/auth';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { auth } from './firebaseConfig';
import LoginPage from './pages/LoginPage';
import FirstLoginPage from './pages/FirstLoginPage';
import EmailVerificationPendingPage from './pages/EmailVerificationPendingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import OrientationWarning from './components/OrientationWarning';
import './App.css';

// Lazy-loaded pages
const NewHome = lazy(() => import('./pages/NewHome'));
const AdvancedExercisePage = lazy(() => import('./pages/AdvancedExercisePage'));
const DrillExercisePage = lazy(() => import('./pages/DrillExercisePage'));
const ChronoExercisePage = lazy(() => import('./pages/ChronoExercisePage'));
const ChallengePage = lazy(() => import('./pages/ChallengePage'));
const RouletteMainPage = lazy(() => import('./pages/RouletteMainPage'));
const RouletteExercisePage = lazy(() => import('./pages/RouletteExercisePage'));
const DrillMainPage = lazy(() => import('./pages/DrillMainPage'));
const ChronoMainPage = lazy(() => import('./pages/ChronoMainPage'));
const JeuLibreHubPage = lazy(() => import('./pages/JeuLibreHubPage'));
const DrumlessSelectionPage = lazy(() => import('./pages/DrumlessSelectionPage'));
const DrumlessPlayerPage = lazy(() => import('./pages/DrumlessPlayerPage'));
const DrumlessAdminPage = lazy(() => import('./pages/DrumlessAdminPage'));
const SongsAdminPage = lazy(() => import('./pages/SongsAdminPage'));
const DiscoveryAdminPage = lazy(() => import('./pages/DiscoveryAdminPage'));
const SongsSelectionPage = lazy(() => import('./pages/SongsSelectionPage'));
const SongsPlayerPage = lazy(() => import('./pages/SongsPlayerPage'));
const EcouteReproductPage = lazy(() => import('./pages/EcouteReproductPage'));
const ReproduireRythmesPage = lazy(() => import('./pages/ReproduireRythmesPage'));
const MemorizationSelectionPage = lazy(() => import('./pages/MemorizationSelectionPage'));
const MemorizationExercisePage = lazy(() => import('./pages/MemorizationExercisePage'));
const QuizSelectionPage = lazy(() => import('./pages/QuizSelectionPage'));
const QuizExercisePage = lazy(() => import('./pages/QuizExercisePage'));
const QuizLectureSelectionPage = lazy(() => import('./pages/QuizLectureSelectionPage'));
const QuizLectureExercisePage = lazy(() => import('./pages/QuizLectureExercisePage'));
const DecouvertePage = lazy(() => import('./pages/DecouvertePage'));
const DecouvertePreparationPage = lazy(() => import('./pages/DecouvertePreparationPage'));
const DiscoveryRhythmPage = lazy(() => import('./pages/DiscoveryRhythmPage'));
const StatsPage = lazy(() => import('./pages/StatsPage'));
const MidiCheckPage = lazy(() => import('./pages/MidiCheckPage'));

const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ProgressPage = lazy(() => import('./pages/ProgressPage'));
const RankingPage = lazy(() => import('./pages/RankingPage'));
const ChallengeLeaderboardsPage = lazy(() => import('./pages/ChallengeLeaderboardsPage'));
const TeacherDashboard = lazy(() => import('./pages/TeacherDashboard'));
const CoursAdminPage = lazy(() => import('./pages/CoursAdminPage'));
const CoursSelectionPage = lazy(() => import('./pages/CoursSelectionPage'));
const CoursPlayerPage = lazy(() => import('./pages/CoursPlayerPage'));
const TeacherSongsAdminPage = lazy(() => import('./pages/TeacherSongsAdminPage'));
const TeacherSongsSelectionPage = lazy(() => import('./pages/TeacherSongsSelectionPage'));
const MonProfPage = lazy(() => import('./pages/MonProfPage'));
const TeacherSongsPlayerPage = lazy(() => import('./pages/TeacherSongsPlayerPage'));
const ModernAdminPage = lazy(() => import('./pages/ModernAdminPage'));
const AdminApp = lazy(() => import('./admin/AdminApp'));
const LectureProgressionMainPage = lazy(() => import('./pages/LectureProgressionMainPage'));
const LectureProgressionExercisePage = lazy(() => import('./pages/LectureProgressionExercisePage'));
const LectureChallengePage = lazy(() => import('./pages/LectureChallengePage'));
const BreaksDrillHubPage = lazy(() => import('./pages/BreaksDrillHubPage'));
const BreaksMainPage = lazy(() => import('./pages/BreaksMainPage'));
const BreaksExercisePage = lazy(() => import('./pages/BreaksExercisePage'));
const MetronomeSelectionPage = lazy(() => import('./pages/MetronomeSelectionPage'));
const RythmesHubPage = lazy(() => import('./pages/RythmesHubPage'));
const MetronomeChallengePage = lazy(() => import('./pages/MetronomeChallengePage'));
const ParcoursHomePage = lazy(() => import('./pages/ParcoursHomePage'));
const ParcoursAdminPage = lazy(() => import('./pages/ParcoursAdminPage'));
const BiweeklyChallengeAdminPage = lazy(() => import('./pages/BiweeklyChallengeAdminPage'));
const TeacherAdminPage = lazy(() => import('./pages/TeacherAdminPage'));
const Settings = lazy(() => import('./pages/Settings'));
const DebugPage = lazy(() => import('./pages/DebugPage'));

// Map des routes vers leur page parente logique
// Utilisée par Tom2BackListener pour garantir une navigation cohérente
const getParentRoute = (pathname) => {
  // Exercices de rythmes standards
  if (/^\/exercise\//.test(pathname)) return '/rythmes';
  // Roulette exercice → sélection
  if (/^\/roulette\//.test(pathname)) return '/roulette';
  // Metronome challenge exercice → sélection
  if (/^\/metronome-challenge\/.+/.test(pathname)) return '/metronome-challenge';
  // Quiz exercice → sélection
  if (/^\/quiz\/.+/.test(pathname)) return '/quiz';
  // Breaks exercice → parcours breaks
  if (/^\/breaks\/play\//.test(pathname)) return '/breaks/parcours';
  // Lecture exercice → sélection
  if (/^\/lecture\/play\//.test(pathname)) return '/lecture';
  // Lecture challenge → hub challenges (plus de page de sélection intermédiaire)
  if (/^\/lecture-challenge\/play/.test(pathname)) return '/challenges';
  // Drumless player → sélection
  if (/^\/drumless\/play\//.test(pathname)) return '/drumless';
  // Mémorisation exercice → sélection
  if (/^\/ecoute-reproduit\/memorisation\/.+/.test(pathname)) return '/ecoute-reproduit/memorisation';
  // Découverte exercices → hub découverte
  if (/^\/decouverte\//.test(pathname)) return '/decouverte';
  // Pages enfants challenges → hub
  if (pathname === '/drill' || pathname === '/chrono' || pathname === '/roulette') return '/challenges';
  // Metronome Challenge sélection → hub rythmes (point d'entrée réel via RythmesHubPage,
  // /challenges ne le référence plus — mapping resté sur /challenges par erreur après le
  // déplacement de la fonctionnalité, incident du 2026-08-30)
  if (pathname === '/metronome-challenge') return '/rythmes';
  // Pages enfants écoute → hub
  if (pathname === '/ecoute-reproduit/reproduire-rythmes' || pathname === '/ecoute-reproduit/memorisation' || pathname === '/quiz') return '/ecoute-reproduit';
  // Pages enfants breaks → hub
  if (pathname === '/breaks/parcours') return '/breaks';
  // Hubs de premier niveau + pages standalone → accueil
  if (
    pathname === '/challenges' ||
    pathname === '/rythmes' ||
    pathname === '/ecoute-reproduit' ||
    pathname === '/decouverte' ||
    pathname === '/lecture' ||
    pathname === '/breaks' ||
    pathname === '/drumless' ||
    pathname === '/parcours' ||
    pathname === '/jeu-libre' ||
    pathname === '/mon-prof' ||
    pathname === '/chansons' ||
    pathname.startsWith('/chansons/play/') ||
    pathname === '/profile' ||
    pathname === '/progress' ||
    pathname === '/ranking' ||
    pathname === '/settings'
  ) return '/';
  // Pas de parent connu → fallback navigate(-1)
  return null;
};

// Recharge silencieusement le site au changement de page si une nouvelle version est en ligne
const StaleVersionReloader = () => {
  const { pathname } = useLocation();
  const first = React.useRef(true);
  React.useEffect(() => {
    if (first.current) { first.current = false; return; }
    reloadIfStale();
  }, [pathname]);
  return null;
};

// Écoute globale du Tom 2 (MIDI note 45) → navigate vers parent logique si non verrouillé
const Tom2BackListener = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isTom2Locked } = useTom2Lock();
  // Refs pour lire pathname et state courants sans recréer le listener MIDI à chaque navigation
  const pathnameRef = React.useRef(location.pathname);
  const fromParcoursRef = React.useRef(location.state?.fromParcours === true);
  const backToRef = React.useRef(location.state?.backTo ?? null);
  useEffect(() => {
    pathnameRef.current = location.pathname;
    fromParcoursRef.current = location.state?.fromParcours === true;
    backToRef.current = location.state?.backTo ?? null;
  }, [location.pathname, location.state]);

  useEffect(() => {
    if (!navigator.requestMIDIAccess) return;

    let midiAccess = null;

    const handleMidiMessage = (event) => {
      const [status, note] = event.data;
      const isNoteOn = (status & 0xf0) === 0x90 && event.data[2] > 0;
      if (isNoteOn && note === 45 && !isTom2Locked()) {
        // Si on vient du parcours, retour toujours à l'accueil
        if (fromParcoursRef.current) {
          navigate('/');
          return;
        }
        const parent = backToRef.current || getParentRoute(pathnameRef.current);
        if (parent) {
          navigate(parent);
        } else {
          navigate(-1);
        }
      }
    };

    navigator.requestMIDIAccess({ sysex: false }).then((access) => {
      midiAccess = access;
      access.inputs.forEach((input) => {
        input.addEventListener('midimessage', handleMidiMessage);
      });
      access.onstatechange = () => {
        access.inputs.forEach((input) => {
          input.removeEventListener('midimessage', handleMidiMessage);
          input.addEventListener('midimessage', handleMidiMessage);
        });
      };
    }).catch(() => {});

    return () => {
      if (midiAccess) {
        midiAccess.inputs.forEach((input) => {
          input.removeEventListener('midimessage', handleMidiMessage);
        });
      }
    };
  }, [navigate, isTom2Locked]);

  return null;
};

const PageLoader = () => (
  <div className="flex justify-center items-center h-screen">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
      <p className="text-lg">Chargement...</p>
    </div>
  </div>
);

// Lien « mot de passe oublié » : capturé AVANT le montage du Router. Sinon la redirection
// automatique de la route "/" vers "/login" (ProtectedRoute) écrase la navigation et efface
// les paramètres du lien (oobCode) — constaté le 2026-10-15 : le lien arrivait sur /login.
(() => {
  try {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const oobCode = params.get('oobCode');
    console.log('[AuthAction] URL au chargement', { path: window.location.pathname, mode, hasOobCode: !!oobCode });
    if (mode === 'resetPassword' && oobCode) {
      window.history.replaceState(null, '', `/reset-password?oobCode=${encodeURIComponent(oobCode)}`);
    }
  } catch (e) {
    console.warn('[AuthAction] lecture de l\'URL impossible', e);
  }
})();

// Handler Firebase auth actions (verifyEmail, resetPassword…) présents dans l'URL
const FirebaseAuthActionHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshEmailVerification } = useAuth();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const oobCode = params.get('oobCode');
    // Lien « mot de passe oublié » : page dédiée (jamais l'onboarding)
    if (mode === 'resetPassword' && oobCode) {
      navigate(`/reset-password?oobCode=${encodeURIComponent(oobCode)}`, { replace: true });
      return;
    }
    if (mode === 'verifyEmail' && oobCode) {
      applyActionCode(auth, oobCode)
        .then(async () => {
          console.log('✅ Email vérifié avec succès');
          const verified = await refreshEmailVerification();
          if (verified) {
            // Même navigateur : utilisateur déjà connecté → aller directement à /first-login
            navigate('/first-login', { replace: true });
          } else {
            // Autre navigateur/appareil : l'utilisateur doit se connecter
            navigate('/login?verified=1', { replace: true });
          }
        })
        .catch((err) => {
          console.error('❌ Erreur vérification email:', err);
          logAuthFailure('verify_email_link', err);
          navigate('/login?verified=error', { replace: true });
        });
    }
  }, [location.search, navigate, refreshEmailVerification]);
  return null;
};

// ProtectedAdminRoute - réservé aux comptes avec le custom claim { admin: true }
const ProtectedAdminRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    if (!currentUser) { setIsAdmin(false); return; }
    currentUser.getIdTokenResult().then(result => {
      setIsAdmin(result.claims.admin === true);
    }).catch(() => setIsAdmin(false));
  }, [currentUser]);

  if (loading || isAdmin === null) return <PageLoader />;
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  return children;
};

// ProtectedRoute component
const ProtectedRoute = ({ children }) => {
  const { currentUser, userProfile, loading, isFirstLogin, isPendingEmailVerification } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Email non vérifié : bloquer l'accès jusqu'à la vérification
  if (isPendingEmailVerification) {
    return <Navigate to="/verify-email" replace />;
  }

  // Si c'est la première connexion et qu'on n'est pas déjà sur /first-login
  if (isFirstLogin && location.pathname !== '/first-login') {
    return <Navigate to="/first-login" replace />;
  }

  // Gate renforcé : visiteur anonyme ou élève sans prof rattaché → chapitre 1 uniquement
  if (isRestrictedAccount(userProfile, currentUser)
      && !isPathAllowedForRestricted(location.pathname, location.state?.fromParcours === true, isAnonymousUser(currentUser))) {
    const reason = getRestrictionReason(userProfile, currentUser);
    return (
      <AccessDeniedModal
        isOpen
        onClose={() => navigate('/', { replace: true })}
        message={getAccessDeniedMessage(userProfile?.profile?.accountType, reason)}
      />
    );
  }

  return children;
};

// ProtectedChallenge component - pour Drill, Chrono (réservés aux student/email)
const ProtectedChallenge = ({ children }) => {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState(null);

  const accountType = userProfile?.profile?.accountType || 'email';
  const restrictionReason = getRestrictionReason(userProfile, currentUser);
  const hasAccess = hasAccessToChallenge(accountType) && !restrictionReason;

  if (!hasAccess) {
    if (!showAccessDenied) {
      setShowAccessDenied(true);
      setAccessDeniedMessage(getAccessDeniedMessage(accountType, restrictionReason));
    }

    return (
      <>
        <AccessDeniedModal
          isOpen={showAccessDenied}
          onClose={() => {
            setShowAccessDenied(false);
            navigate('/');
          }}
          message={accessDeniedMessage}
        />
        <div className="flex justify-center items-center h-screen bg-gray-50">
          <div className="text-center">
            <p className="text-lg text-gray-600">Vérification de l'accès...</p>
          </div>
        </div>
      </>
    );
  }

  return children;
};

const ProtectedExercise = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { exercises, isLoading } = useExercises();
  const { userProfile, currentUser } = useAuth();
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState(null);
  const fromParcours = location.state?.fromParcours === true;

  useEffect(() => {
    if (!isLoading && (!exercises[exerciseId])) {
      navigate('/');
    }
  }, [exerciseId, navigate, exercises, isLoading]);

  if (isLoading) {
    return <div>Chargement...</div>;
  }
  if (!exercises[exerciseId]) {
    return <Navigate to="/" />
  }

  const exercise = exercises[exerciseId];
  const accountType = userProfile?.profile?.accountType || 'email';
  const nodeId = exercise.nodeId;

  // Vérifier l'accès pour les comptes restreints : anonymes + élèves sans prof (bypasser si vient du parcours)
  const restrictionReason = getRestrictionReason(userProfile, currentUser);
  if (restrictionReason && !fromParcours) {
    // Vérifier l'accès à cet exercice (périmètre anonyme : 3 premiers exercices du nœud 1.1)
    const hasAccess = hasAccessToExercise('anonymous', nodeId, 0);

    if (!hasAccess) {
      // Afficher le modal d'upgrade
      if (!showAccessDenied) {
        setShowAccessDenied(true);
        setAccessDeniedMessage(getAccessDeniedMessage(accountType, restrictionReason));
      }

      return (
        <>
          <AccessDeniedModal
            isOpen={showAccessDenied}
            onClose={() => {
              setShowAccessDenied(false);
              navigate('/');
            }}
            message={accessDeniedMessage}
          />
          <div className="flex justify-center items-center h-screen bg-gray-50">
            <div className="text-center">
              <p className="text-lg text-gray-600">Vérification de l'accès...</p>
            </div>
          </div>
        </>
      );
    }
  }

  // Déterminer le type d'exercice en utilisant la propriété 'type' de l'exercice
  const exerciseType = exercise.type || 'standard';

  // Rendre le composant approprié selon le type d'exercice
  if (exerciseType === 'drill') return <DrillExercisePage />;
  if (exerciseType === 'chrono') return <ChronoExercisePage />;
  return <AdvancedExercisePage />;
};

function App() {
  useEffect(() => {
    // Débloquer l'AudioContext dès le premier geste utilisateur (requis par Chrome/Safari).
    // keydown en plus de click/touchstart : un rechargement page (Cmd+Shift+R) ne compte pas
    // comme clic sur le document, donc si l'utilisateur interagit ensuite au clavier plutôt
    // qu'à la souris, click seul ratait cette occasion de débloquer le contexte avant qu'une
    // frappe MIDI (qui elle n'est jamais reconnue comme geste utilisateur) n'en ait besoin.
    const unlock = () => { initAudio().catch(() => {}); };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
  }, []);

  return (
    <ThemeProvider>
    <AuthProvider>
      <ExercisesProvider>
        <NotationProvider>
          <ParcoursProvider>
          <Tom2LockProvider>
          <Router>
            <FirebaseAuthActionHandler />
            <Tom2BackListener />
            <StaleVersionReloader />
            <div className="App">
              <OrientationWarning />
              <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/first-login" element={<FirstLoginPage />} />
              <Route path="/verify-email" element={<EmailVerificationPendingPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/politique-confidentialite" element={<PrivacyPolicyPage />} />
              <Route path="/mentions-legales" element={<MentionsLegalesPage />} />

              {/* Protected routes */}
              <Route path="/" element={<ProtectedRoute><NewHome /></ProtectedRoute>} />
              <Route path="/exercise" element={<Navigate to="/" replace />} />
              <Route path="/exercise/:exerciseId" element={
                <ProtectedRoute>
                  <React.Fragment key={window.location.pathname}>
                    <ProtectedExercise />
                  </React.Fragment>
                </ProtectedRoute>
              } />
              <Route path="/stats" element={<ProtectedRoute><StatsPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
              <Route path="/ranking" element={<ProtectedRoute><RankingPage /></ProtectedRoute>} />
              <Route path="/teacher-dashboard" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
              <Route path="/teacher/cours" element={<ProtectedRoute><CoursAdminPage /></ProtectedRoute>} />
              <Route path="/teacher/songs" element={<ProtectedRoute><TeacherSongsAdminPage /></ProtectedRoute>} />
              <Route path="/challenge-leaderboards" element={<ProtectedRoute><ChallengeLeaderboardsPage /></ProtectedRoute>} />
              <Route path="/rythmes" element={<ProtectedRoute><RythmesHubPage /></ProtectedRoute>} />
              <Route path="/challenges" element={<ProtectedRoute><ChallengePage /></ProtectedRoute>} />
              <Route path="/roulette" element={<ProtectedRoute><RouletteMainPage /></ProtectedRoute>} />
              <Route path="/roulette/:tierId" element={<ProtectedRoute><RouletteExercisePage /></ProtectedRoute>} />
              <Route path="/metronome-challenge" element={<ProtectedRoute><MetronomeSelectionPage /></ProtectedRoute>} />
              <Route path="/metronome-challenge/:tierId" element={<ProtectedRoute><MetronomeChallengePage /></ProtectedRoute>} />
              <Route path="/metronome-challenge/:tierId/:seed" element={<ProtectedRoute><MetronomeChallengePage /></ProtectedRoute>} />
              <Route path="/drill" element={<ProtectedRoute><ProtectedChallenge><DrillMainPage /></ProtectedChallenge></ProtectedRoute>} />
              <Route path="/mon-prof" element={<ProtectedRoute><MonProfPage /></ProtectedRoute>} />
              <Route path="/cours" element={<ProtectedRoute><ProtectedChallenge><CoursSelectionPage /></ProtectedChallenge></ProtectedRoute>} />
              <Route path="/cours/:exerciseId" element={<ProtectedRoute><ProtectedChallenge><CoursPlayerPage /></ProtectedChallenge></ProtectedRoute>} />
              <Route path="/mes-chansons" element={<ProtectedRoute><ProtectedChallenge><TeacherSongsSelectionPage /></ProtectedChallenge></ProtectedRoute>} />
              <Route path="/mes-chansons/play/:songId" element={<ProtectedRoute><ProtectedChallenge><TeacherSongsPlayerPage /></ProtectedChallenge></ProtectedRoute>} />
              <Route path="/chrono" element={<ProtectedRoute><ProtectedChallenge><ChronoMainPage /></ProtectedChallenge></ProtectedRoute>} />
              <Route path="/lecture" element={<ProtectedRoute><LectureProgressionMainPage /></ProtectedRoute>} />
              <Route path="/lecture/play/:levelId" element={<ProtectedRoute><LectureProgressionExercisePage /></ProtectedRoute>} />
              <Route path="/lecture-challenge/play" element={<ProtectedRoute><LectureChallengePage /></ProtectedRoute>} />
              <Route path="/breaks" element={<ProtectedRoute><BreaksDrillHubPage /></ProtectedRoute>} />
              <Route path="/breaks/parcours" element={<ProtectedRoute><BreaksMainPage /></ProtectedRoute>} />
              <Route path="/breaks/play/:levelId" element={<ProtectedRoute><BreaksExercisePage /></ProtectedRoute>} />
              <Route path="/jeu-libre" element={<ProtectedRoute><JeuLibreHubPage /></ProtectedRoute>} />
              <Route path="/chansons" element={<ProtectedRoute><SongsSelectionPage /></ProtectedRoute>} />
              <Route path="/chansons/play/:songId" element={<ProtectedRoute><SongsPlayerPage /></ProtectedRoute>} />
              <Route path="/drumless" element={<ProtectedRoute><DrumlessSelectionPage filterPartition={false} /></ProtectedRoute>} />
              <Route path="/drumless/play/:songId" element={<ProtectedRoute><DrumlessPlayerPage /></ProtectedRoute>} />
              <Route path="/ecoute-reproduit" element={<ProtectedRoute><EcouteReproductPage /></ProtectedRoute>} />
              <Route path="/ecoute-reproduit/reproduire-rythmes" element={<ProtectedRoute><ReproduireRythmesPage /></ProtectedRoute>} />
              <Route path="/ecoute-reproduit/memorisation" element={<ProtectedRoute><MemorizationSelectionPage /></ProtectedRoute>} />
              <Route path="/ecoute-reproduit/memorisation/:tierId" element={<ProtectedRoute><MemorizationExercisePage /></ProtectedRoute>} />
              <Route path="/quiz" element={<ProtectedRoute><QuizSelectionPage /></ProtectedRoute>} />
              <Route path="/quiz/:tierId" element={<ProtectedRoute><QuizExercisePage /></ProtectedRoute>} />
              <Route path="/quiz-lecture" element={<ProtectedRoute><QuizLectureSelectionPage /></ProtectedRoute>} />
              <Route path="/quiz-lecture/:groupId" element={<ProtectedRoute><QuizLectureExercisePage /></ProtectedRoute>} />
              <Route path="/parcours" element={<ProtectedRoute><ParcoursHomePage /></ProtectedRoute>} />
              <Route path="/decouverte" element={<ProtectedRoute><DecouvertePage /></ProtectedRoute>} />
              <Route path="/decouverte/preparation" element={<ProtectedRoute><DecouvertePreparationPage /></ProtectedRoute>} />
              <Route path="/decouverte/rythmique/:exerciseId" element={<ProtectedRoute><DiscoveryRhythmPage /></ProtectedRoute>} />
              <Route path="/midi-check" element={<ProtectedRoute><MidiCheckPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/admin/*" element={<AdminGuard><AdminApp /></AdminGuard>} />
              <Route path="/admin/ancien" element={<ProtectedAdminRoute><ModernAdminPage /></ProtectedAdminRoute>} />
              <Route path="/admin/parcours" element={<ProtectedAdminRoute><ParcoursAdminPage /></ProtectedAdminRoute>} />
              <Route path="/admin/biweekly-challenge" element={<ProtectedAdminRoute><BiweeklyChallengeAdminPage /></ProtectedAdminRoute>} />
              <Route path="/admin/drumless-songs" element={<ProtectedAdminRoute><DrumlessAdminPage /></ProtectedAdminRoute>} />
              <Route path="/admin/songs" element={<ProtectedAdminRoute><SongsAdminPage /></ProtectedAdminRoute>} />
              <Route path="/admin/teachers" element={<ProtectedAdminRoute><TeacherAdminPage /></ProtectedAdminRoute>} />
              <Route path="/admin/decouverte-tracks" element={<ProtectedAdminRoute><DiscoveryAdminPage /></ProtectedAdminRoute>} />
              <Route path="/debug" element={<ProtectedAdminRoute><DebugPage /></ProtectedAdminRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
              </Suspense>
          </div>
        </Router>
          </Tom2LockProvider>
        </ParcoursProvider>
        </NotationProvider>
      </ExercisesProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;